import am4geodata_worldLow from '@amcharts/amcharts4-geodata/worldLow'
import * as am4core from '@amcharts/amcharts4/core'
import * as am4maps from '@amcharts/amcharts4/maps'
import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  ViewChild
} from '@angular/core'

import { AggregationDataset } from '../../../shared/interfaces/visualizations/aggregation-dataset.interface'
import { SmartLinkService } from '../../../shared/services/smart-link.service'
import { MapCountryItem } from './interfaces/map-country-item.interface'
import { iso3ToIso2 } from './iso3-to-iso2'

@Component({
  selector: 'app-world-map',
  templateUrl: './world-map.component.html',
  styleUrls: ['./world-map.component.scss']
})
export class WorldMapComponent implements OnChanges {
  @ViewChild('worldMap', { static: true }) chartContainer: ElementRef

  @Input() dataset: AggregationDataset

  private chart: any
  private height = 500

  private zeroIncentiveColor = '#EBEEF1'
  private noDataColor = '#F5F7F9'
  private hoverColor = '#C4CAFF'
  private tooltipColor = '#023047'
  private strokeColor = '#B9B5B8'

  // We hide legend by default and show it after chart is rendered to avoid flickering.
  showLegend = false

  countryGroups: { name: string; color: string }[] = [
    { name: 'Mixed', color: '#FFC304' },
    { name: 'Single', color: '#FF7918' },
    { name: 'Dual', color: '#66D4E8' },
    { name: 'Multiple', color: '#74E0B1' }
  ]

  constructor(private smartLinkService: SmartLinkService) {}

  ngOnChanges(): void {
    this.showLegend = false

    setTimeout(() => {
      this.buildWorldMap()
      this.showLegend = true
    }, 0)
  }

  buildWorldMap(): void {
    // Set chart container height from calculation in parent.
    this.chartContainer.nativeElement.style.height = `${this.height}px`

    this.chart = am4core.create('world-map', am4maps.MapChart)
    this.chart.geodata = am4geodata_worldLow
    this.chart.projection = new am4maps.projections.Miller()
    this.chart.zoomControl = new am4maps.ZoomControl()
    this.chart.chartContainer.wheelable = false

    const polygonSeries = this.chart.series.push(new am4maps.MapPolygonSeries())

    polygonSeries.useGeodata = true
    // Tooltip style.
    polygonSeries.tooltip.fitPointerToBounds = true
    polygonSeries.tooltip.getFillFromObject = false
    polygonSeries.tooltip.fillOpacity = 100
    polygonSeries.tooltip.strokeOpacity = 1
    polygonSeries.tooltip.valign = 'top'
    polygonSeries.tooltip.background.fill = am4core.color(this.tooltipColor)
    polygonSeries.tooltip.background.stroke = am4core.color(this.tooltipColor)
    polygonSeries.tooltip.background.filters.clear()
    polygonSeries.tooltip.fontSize = '12px'
    polygonSeries.tooltip.fontFamily =
      'Helvetica Neue, Helvetica, Arial, sans-serif'

    // Configure series
    const polygonTemplate = polygonSeries.mapPolygons.template

    polygonTemplate.tooltipText = `[bold]{name}[/]
    Incentives : {count}`

    polygonTemplate.stroke = am4core.color(this.strokeColor)
    polygonTemplate.strokeWidth = 1
    polygonTemplate.strokeOpacity = 0.4
    polygonTemplate.properties.fill = am4core.color(this.noDataColor)
    polygonTemplate.cursorOverStyle = am4core.MouseCursorStyle.pointer
    polygonTemplate.tooltipY = 100

    // Create hover state and set alternative fill color
    const hoverState = polygonTemplate.states.create('hover')
    hoverState.properties.fill = am4core.color(this.hoverColor)

    // Remove Antarctica (No tax incentives there!).
    polygonSeries.exclude = ['AQ']

    // Add data.
    polygonSeries.data = this.dataset.data.map((country) => ({
      id: this.convertIso3IntoIso2(country.code),

      color: this.getCountryColor(country),
      name: country.label,
      count: country.count,
      description: country.description,
      link: country.value === 0 ? null : country.link
    }))

    // Bind "fill" property to "color" key in data
    polygonTemplate.propertyFields.fill = 'color'

    polygonTemplate.events.on('hit', (event: any) => {
      const country = event.target.dataItem.dataContext

      if (country.link) {
        this.smartLinkService.goToLink(country.link)
      }
    })

    polygonTemplate.events.on('over', (event: any) => {
      const country = event.target.dataItem.dataContext

      if (!country.hasOwnProperty('count')) {
        // Hack: do not show tooltip for countries without incentiveCount.
        event.target.showTooltipOn = 'hit'
      }
    })
  }

  convertIso3IntoIso2(iso3: string): any {
    return iso3ToIso2[iso3]
  }

  // We get the country color based on terms included on the description.
  getCountryColor(country: MapCountryItem): am4core.Color {
    if (country.count === 0) {
      return am4core.color(this.zeroIncentiveColor)
    }

    // We return the first color that matches the description. It means that the order of this.countryGroups is important.
    return am4core.color(
      this.countryGroups.filter((group) =>
        country.value.includes(group.name)
      )[0]?.color
    )
  }
}
